import { BOGGLE_ACHIEVEMENT_DEFINITIONS } from '~/boggle/config/achievements/registry'
import type {
  BoggleAchievementDefinition,
  BoggleAchievementId,
} from '~/boggle/config/achievements/types'
import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'

export type RunSummaryAchievementUnlockEvent = {
  achievementId: BoggleAchievementId
  description: string
  tierId: string
  tierLabel: string
  title: string
  type: 'achievement_unlocked'
}

export type RunSummaryRatingRevealEvent = {
  description: string
  ratingId: BoggleRunSummaryRatingId
  ratingLabel: string
  title: string
  type: 'rating_revealed'
}

export type RunSummaryAchievementEvent =
  | RunSummaryAchievementUnlockEvent
  | RunSummaryRatingRevealEvent

const RATING_LABELS = new Map(
  BOGGLE_RUN_SUMMARY_RATINGS.map(rating => [rating.id, rating.label]),
)

function findTierDefinition(
  definitions: readonly BoggleAchievementDefinition[],
  tierId: string,
) {
  for (const definition of definitions) {
    const tier = definition.tiers.find(candidate => candidate.id === tierId)
    if (tier) {
      return { definition, tier }
    }
  }
  return null
}

export function buildRunSummaryAchievementEvents({
  definitions = BOGGLE_ACHIEVEMENT_DEFINITIONS,
  newlyRevealedRatingIds,
  newlyUnlockedTierIds,
}: {
  definitions?: readonly BoggleAchievementDefinition[]
  newlyRevealedRatingIds: readonly BoggleRunSummaryRatingId[]
  newlyUnlockedTierIds: readonly string[]
}): readonly RunSummaryAchievementEvent[] {
  const events: RunSummaryAchievementEvent[] = []
  const seenRatings = new Set<BoggleRunSummaryRatingId>()
  const seenTierIds = new Set<string>()

  for (const ratingId of newlyRevealedRatingIds) {
    if (seenRatings.has(ratingId)) {
      continue
    }
    const ratingLabel = RATING_LABELS.get(ratingId)
    if (!ratingLabel) {
      continue
    }
    seenRatings.add(ratingId)
    events.push({
      description: `${ratingLabel} goals are now visible.`,
      ratingId,
      ratingLabel,
      title: `${ratingLabel} Achievements`,
      type: 'rating_revealed',
    })
  }

  for (const tierId of newlyUnlockedTierIds) {
    if (seenTierIds.has(tierId)) {
      continue
    }
    const match = findTierDefinition(definitions, tierId)
    if (!match) {
      continue
    }
    seenTierIds.add(tierId)
    events.push({
      achievementId: match.definition.id,
      description: match.tier.description,
      tierId,
      tierLabel: match.tier.label,
      title: match.definition.title,
      type: 'achievement_unlocked',
    })
  }

  return events
}
